import chunk from 'lodash/chunk';
import isEqual from 'lodash/isEqual';
import {
  AnimationCode,
  RgbColor,
  RgbtColor,
  parseHtmlColor,
  unparseHtmlColor,
} from 'pikaparam';

import {Constants} from '../../../../../constants';
import {colors} from '../../../../../styles';
import {
  DeviceModelVariant,
  LightingPattern,
  MoodLight,
} from '../../../../types';
import {getLightingPattern, getLumaAnimation} from '../../../../utils';
import {MoodLightEvaluator} from '../../MoodLightEvaluator';
import {Scratchpad, ScratchpadVersion} from '../../Scratchpad';
import {AnyDeviceProperties, NvmIndex} from '../../pikaparam';
import {getNvmIndex} from '../../util';
import {IUnitClient} from '../UnitClient';
import {
  ColorLight,
  type IUnitLedApi,
  type ReadLightOptions,
  type ReadLightResult,
  type Target,
  type WriteLightOptions,
  isMoodLight,
} from './IUnitLedApi';

export interface WriteColorLightOptions {
  target: Target;
  light: ColorLight;
  metadata: Scratchpad;
}

export interface WriteMoodLightOptions {
  target: Target;
  light: MoodLight;
  metadata: Scratchpad;
}

const fallbackColor = {
  color: colors.defaultColor,
  pattern: LightingPattern.STEADY,
};

export class UnitLedApiV2 implements IUnitLedApi {
  constructor(private client: IUnitClient) {}

  public async readActiveLedArray(): Promise<number[]> {
    const value = await this.client.read<RgbtColor>('activeLedColorApi2');

    return Array.from(new Uint8Array(value.bytes.buffer));
  }

  public async setPartyMode() {
    const rgbt = new RgbtColor([...Constants.PARTY_MODE.T]);

    await this.client.write<RgbtColor>(`lanternColorApi2`, rgbt);
  }

  public async readLight(options: ReadLightOptions): Promise<ReadLightResult> {
    const {target} = options;

    const colorBuffer = await this.client.read<RgbtColor>(`${target}ColorApi2`);
    const colorScratchpad = await this.client.read<number[]>(
      `${target}Scratchpad`,
    );

    const isPartyMode = this.isPartyMode(colorBuffer.bytes);
    const lightingPattern =
      target === 'lantern' && colorBuffer.lumaAnim !== undefined
        ? getLightingPattern(colorBuffer.lumaAnim)
        : undefined;

    const pattern = isPartyMode ? LightingPattern.PARTY_MODE : lightingPattern;

    // TODO: Get fallback color based on index
    const color = colorBuffer.asRgb
      ? unparseHtmlColor(colorBuffer.asRgb)
      : fallbackColor.color;

    if (!colorScratchpad.length) return {light: {color, pattern}};

    const metadata = Scratchpad.parse(colorScratchpad);

    const {moodLight} = metadata.segregate();

    return {light: moodLight ?? {color, pattern}, metadata: metadata};
  }

  public async writeLight(options: WriteLightOptions): Promise<void> {
    const metadata =
      options.metadata ?? new Scratchpad({version: ScratchpadVersion.BLANK});

    if (isMoodLight(options.light))
      return this.writeMoodLight({
        target: options.target,
        light: options.light,
        metadata,
      });

    return this.writeColorLight({
      target: options.target,
      light: options.light,
      metadata,
    });
  }

  private async writeColorLight(
    options: WriteColorLightOptions,
  ): Promise<void> {
    const {light, target, metadata} = options;

    const data = RgbtColor.fromRgb({
      ...parseHtmlColor(light.color),
      // TODO: Fix typing here
      lumaAnim: getLumaAnimation(
        light.pattern ?? LightingPattern.STEADY,
      ) as unknown as AnimationCode,
    });

    await this.client.write<RgbtColor>(`${target}ColorApi2`, data);
    await this.client.write<number[]>(
      `${target}Scratchpad`,
      metadata.serialize(),
    );
  }

  private async writeMoodLight(options: WriteMoodLightOptions): Promise<void> {
    const {target, light, metadata} = options;

    const index = getNvmIndex(target);

    const alternatedIndex =
      target === 'lantern'
        ? await this.alternateLanternIndexForSmoothTransition(index)
        : index;

    const evaluated = MoodLightEvaluator.evaluate(light, {
      ...light.projector.meta,
      variant: DeviceModelVariant.OG,
    }).value;

    const offsetArray = evaluated.lamp.param.offset;
    const colorArray = evaluated.lamp.param.color;

    const newColorArray = chunk(colorArray, 3).map(([r, g, b]) => ({r, g, b}));

    const colorProperty = `userColorArray${alternatedIndex}` as const;
    await this.client.write<RgbColor[]>(colorProperty, newColorArray);

    if (offsetArray) {
      const offsetProperty = `userOffsetArray${alternatedIndex}` as const;
      await this.client.write<number[]>(offsetProperty, offsetArray);
    }

    // TODO: Resolve this?
    // if (animationArray) {
    //   const animationBuffer = writeAnimNumArrayToAnimFrame(animationArray);
    //   // TODO is this right? should this correspond to the heat profile index?
    //   // looking at the flat implementation, it always seems to write to 0
    //   const aaProperty = `userAnimArray0`;

    //   await this.client.writeCommand<any>(aaProperty, [animationBuffer]);
    // }

    const data = RgbtColor.fromTable({
      luma: evaluated.lamp.param.bright ?? 255,
      speed: evaluated.lamp.param.speed ?? 255,
      lumaAnim: evaluated.lamp.param.anim,
      pllNum: evaluated.lamp.param.plNum,
      pllDenom: evaluated.lamp.param.plDenom,
      offsetIndex:
        Constants.NVM_ACCESS_USER_ARRAY_STARTING_OFFSET + alternatedIndex,
      colorIndex:
        Constants.NVM_ACCESS_USER_ARRAY_STARTING_OFFSET + alternatedIndex,
      colorLength: evaluated.lamp.param.colorLen,
    });

    await this.client.write<RgbtColor>(`${target}ColorApi2`, data);
    await this.client
      .write<number[]>(`${target}Scratchpad`, metadata.serialize())
      // AC firmware has an issue with writing the scratchpad
      .catch(() => void 0);
  }

  private async alternateLanternIndexForSmoothTransition(
    nvmIndex: NvmIndex<AnyDeviceProperties>,
  ): Promise<NvmIndex<AnyDeviceProperties>> {
    const colorArrayIndex = await this.readLanternColorArrayIndex();

    if (
      nvmIndex === colorArrayIndex &&
      colorArrayIndex ===
        Constants.NVM_ARRAY_INDICES.DEFAULT_LANTERN_ARRAY_INDEX
    )
      return Constants.NVM_ARRAY_INDICES
        .ALTERNATIVE_LANTERN_ARRAY_INDEX as NvmIndex<AnyDeviceProperties>;

    return nvmIndex;
  }

  private async readLanternColorArrayIndex() {
    const buffer = await this.client.read<RgbtColor>(`lanternColorApi2`);

    const table = buffer.asTable;

    if (!table) return Constants.NVM_ARRAY_INDICES.DEFAULT_LANTERN_ARRAY_INDEX;

    const {colorIndex} = this.splitIndices(table.offsetIndex);

    return colorIndex;
  }

  private isPartyMode(bytes: ArrayBufferLike) {
    return isEqual(Array.from(new Uint8Array(bytes)), Constants.PARTY_MODE.T);
  }

  private splitIndices(combinedIndices: number) {
    return {
      offsetIndex:
        (combinedIndices >> 4) -
        Constants.NVM_ACCESS_USER_ARRAY_STARTING_OFFSET,
      colorIndex:
        (combinedIndices & 0x0f) -
        Constants.NVM_ACCESS_USER_ARRAY_STARTING_OFFSET,
    };
  }
}
